import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
// High-visibility icons for clear actions
import { 
  FiEye, 
  FiTrash2, 
  FiUserCheck, 
  FiUserX, 
  FiSearch, 
  FiCalendar, 
  FiRefreshCcw,
  FiMoreVertical,
  FiMail,
  FiPhone
} from "react-icons/fi";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function ActiveInactiveVendor() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    status: activeTab,
    fromDate: "",
    toDate: "",
  });

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllVendors`);
      setVendors(res.data.vendors);
    } catch (err) {
      Swal.fire("Error", "Failed to fetch vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, status: activeTab }));
  }, [activeTab]);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const result = await Swal.fire({
      title: `Move to ${newStatus}?`,
      text: `Change vendor status to ${newStatus}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "active" ? "#10b981" : "#f59e0b",
      confirmButtonText: `Yes, ${newStatus} them`,
    });

    if (result.isConfirmed) {
      try {
        await axios.put(`${baseurl}updateVendorStatus/${id}`, { status: newStatus });
        fetchVendors();
      } catch (err) {
        Swal.fire("Error", "Failed to update status", "error");
      }
    }
  };

  const deleteVendor = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Permanently?",
      text: "This vendor and their history will be erased.",
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });

    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteVendor/${id}`);
        fetchVendors();
      } catch (err) {
        Swal.fire("Error", "Failed to delete vendor", "error");
      }
    }
  };

  const filteredVendors = vendors.filter((v) => {
    const matchesStatus = v.status === filters.status;
    const matchesName = v.name.toLowerCase().includes(filters.name.toLowerCase());
    const matchesEmail = v.email.toLowerCase().includes(filters.email.toLowerCase());
    const matchesBusiness = v.business_name.toLowerCase().includes(filters.business_name.toLowerCase());
    return matchesStatus && matchesName && matchesEmail && matchesBusiness;
  });

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <div className="main-content p-4">
          
          {/* Header & Toggle */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
            <div>
              <h4 className="fw-bold mb-0">Vendor Management</h4>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb small mb-0">
                  <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item active">Vendors</li>
                </ol>
              </nav>
            </div>

            <div className="vendor-status-toggle">
              <button 
                className={`toggle-btn ${activeTab === "active" ? "active" : ""}`}
                onClick={() => setActiveTab("active")}
              >
                <FiUserCheck className="me-2" /> Active
              </button>
              <button 
                className={`toggle-btn ${activeTab === "inactive" ? "active" : ""}`}
                onClick={() => setActiveTab("inactive")}
              >
                <FiUserX className="me-2" /> Inactive
              </button>
            </div>
          </div>

          {/* Smart Filter Bar */}
          <div className="card border-0 shadow-sm mb-4 rounded-4">
            <div className="card-body py-3">
              <div className="row g-3 align-items-center">
                <div className="col-lg-4">
                  <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Find vendor by name or business..." 
                      value={filters.name}
                      onChange={(e) => setFilters({...filters, name: e.target.value})}
                    />
                  </div>
                </div>
                <div className="col-lg-3">
                   <div className="search-box">
                    <FiMail className="search-icon" />
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Email address"
                      value={filters.email}
                      onChange={(e) => setFilters({...filters, email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="search-box">
                    <FiCalendar className="search-icon" />
                    <Flatpickr 
                      className="form-control" 
                      placeholder="Filter by Date" 
                      options={{ dateFormat: "Y-m-d" }}
                    />
                  </div>
                </div>
                <div className="col-lg-2">
                  <button className="btn btn-light w-100 d-flex align-items-center justify-content-center gap-2 border" onClick={fetchVendors}>
                    <FiRefreshCcw /> Sync
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Modern Table UI */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 custom-table">
                <thead>
                  <tr>
                    <th className="px-4">Vendor Identity</th>
                    <th>Business Name</th>
                    <th>Contact Info</th>
                    <th>Joined On</th>
                    <th className="text-center px-4">Management Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-5">Loading Vendors...</td></tr>
                  ) : filteredVendors.length > 0 ? (
                    filteredVendors.map((v) => (
                      <tr key={v.id}>
                        <td className="px-4">
                          <div className="d-flex align-items-center gap-3">
                            <div className="avatar">{v.name.charAt(0)}</div>
                            <div>
                              <div className="fw-bold">{v.name}</div>
                              <span className="text-muted smaller">ID: {v.uuid.substring(0,8)}</span>
                            </div>
                          </div>
                        </td>
                        <td><div className="fw-semibold text-secondary">{v.business_name}</div></td>
                        <td>
                          <div className="small"><FiMail className="me-1" /> {v.email}</div>
                          <div className="small text-muted mt-1"><FiPhone className="me-1" /> {v.phone}</div>
                        </td>
                        <td>{new Date(v.created_at).toLocaleDateString()}</td>
                        <td className="px-4 text-center">
                          <div className="action-group">
                            <Link to={`/vendor-view/${v.uuid}`} className="action-btn view" title="View Details">
                              <FiEye />
                            </Link>
                            
                            {v.status === "active" ? (
                              <button onClick={() => toggleStatus(v.id, v.status)} className="action-btn deactivate" title="Disable Vendor">
                                <FiUserX />
                              </button>
                            ) : (
                              <button onClick={() => toggleStatus(v.id, v.status)} className="action-btn activate" title="Enable Vendor">
                                <FiUserCheck />
                              </button>
                            )}

                            <button onClick={() => deleteVendor(v.id)} className="action-btn delete" title="Remove Account">
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-5">No vendors found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        /* Toggle Switch Styling */
        .vendor-status-toggle {
          background: #e4e6eb;
          padding: 4px;
          border-radius: 12px;
          display: flex;
          gap: 4px;
        }
        .toggle-btn {
          border: none;
          padding: 8px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          color: #65676b;
          background: transparent;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
        }
        .toggle-btn.active {
          background: white;
          color: #0d6efd;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        /* Search Box Styling */
        .search-box {
          position: relative;
        }
        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #adb5bd;
        }
        .search-box .form-control {
          padding-left: 38px;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 10px;
        }

        /* Action Buttons Styling */
        .action-group {
          display: flex;
          gap: 8px;
          justify-content: center;
        }
        .action-btn {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          transition: all 0.2s ease;
          background: #f0f2f5;
          color: #4b5563;
          text-decoration: none;
        }
        .action-btn.view:hover { background: #e0e7ff; color: #4338ca; }
        .action-btn.activate:hover { background: #dcfce7; color: #15803d; }
        .action-btn.deactivate:hover { background: #fef3c7; color: #b45309; }
        .action-btn.delete:hover { background: #fee2e2; color: #b91c1c; }

        /* Table Aesthetics */
        .custom-table thead th {
          background: #f8f9fa;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
          color: #6b7280;
          border-bottom: 2px solid #f3f4f6;
          padding: 15px;
        }
        .avatar {
          width: 38px;
          height: 38px;
          background: #0d6efd;
          color: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        .smaller { font-size: 11px; }
      `}</style>
    </>
  );
}

export default ActiveInactiveVendor;